import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAppBySlug } from "@/data/appsCatalog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

export default function ApplicationDetailPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { startParasutConnect } = useAuth();
  const app = useMemo(() => getAppBySlug(String(slug || "")), [slug]);
  const [loading, setLoading] = useState(false);
  const [parasut, setParasut] = useState({
    connected: false,
    company_id: null,
    connected_at: null,
    account_email: "",
    firms: [],
  });
  const [bizimhesap, setBizimhesap] = useState({
    connected: false,
    connected_at: null,
    has_credentials: false,
    has_token: false,
    api_key: "",
    token: "",
    token_masked: "",
    last_test_ok: null,
    last_test_error: "",
  });
  const [bizimhesapBusy, setBizimhesapBusy] = useState(false);
  const [benimpos, setBenimpos] = useState({
    connected: false,
    connected_at: null,
    branch_id: "",
    api_key: "",
    secret_key: "",
  });
  const [benimposBusy, setBenimposBusy] = useState(false);

  const [turkcellEfatura, setTurkcellEfatura] = useState({
    connected: false,
    connected_at: null,
    test_mode: false,
    api_key_masked: null,
    api_key: "",
    test_mode_input: false,
  });
  const [turkcellBusy, setTurkcellBusy] = useState(false);

  const isActive = app?.status === "active";
  const isParasut = app?.slug === "parasut";
  const isBizimhesap = app?.slug === "bizimhesap";
  const isBenimpos = app?.slug === "benimpos";
  const isTurkcellEfatura = app?.slug === "turkcell-efatura";

  useEffect(() => {
    if (!isParasut) return;
    let ignore = false;
    const run = async () => {
      setLoading(true);
      try {
        const [statusRes, firmsRes] = await Promise.all([
          api.get("/parasut/status").catch(() => ({})),
          api.get("/parasut/firms").catch(() => ({})),
        ]);
        if (ignore) return;
        setParasut({
          connected: Boolean(statusRes?.connected),
          company_id: statusRes?.company_id || null,
          connected_at: statusRes?.connected_at || null,
          account_email: firmsRes?.account_email || "",
          firms: Array.isArray(firmsRes?.firms) ? firmsRes.firms : [],
        });
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [isParasut]);

  useEffect(() => {
    if (!isBenimpos) return;
    let ignore = false;
    const run = async () => {
      setLoading(true);
      try {
        const statusRes = await api.get("/benimpos/status").catch(() => ({}));
        if (ignore) return;
        setBenimpos((prev) => ({
          ...prev,
          connected: Boolean(statusRes?.connected),
          connected_at: statusRes?.connected_at || null,
          branch_id: statusRes?.branch_id || "",
        }));
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => { ignore = true; };
  }, [isBenimpos]);

  useEffect(() => {
    if (!isTurkcellEfatura) return;
    let ignore = false;
    const run = async () => {
      setLoading(true);
      try {
        const statusRes = await api.get("/turkcell-efatura/status").catch(() => ({}));
        if (ignore) return;
        const connected = Boolean(statusRes?.connected);
        setTurkcellEfatura((prev) => ({
          ...prev,
          connected,
          connected_at: statusRes?.connected_at || null,
          test_mode: Boolean(statusRes?.test_mode),
          api_key_masked: statusRes?.api_key_masked || null,
        }));
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => { ignore = true; };
  }, [isTurkcellEfatura]);

  useEffect(() => {
    if (!isBizimhesap) return;
    let ignore = false;
    const run = async () => {
      setLoading(true);
      try {
        const statusRes = await api.get("/bizimhesap/status").catch(() => ({}));
        if (ignore) return;
        setBizimhesap((prev) => ({
          ...prev,
          connected: Boolean(statusRes?.connected),
          connected_at: statusRes?.connected_at || null,
          has_credentials: Boolean(statusRes?.has_credentials),
          has_token: Boolean(statusRes?.has_token),
          token_masked: String(statusRes?.token_masked || ""),
          last_test_ok: statusRes?.last_test_ok ?? null,
          last_test_error: String(statusRes?.last_test_error || ""),
        }));
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [isBizimhesap]);

  if (!app) {
    return (
      <div className="page-shell">
        <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Uygulama bulunamadı.
        </div>
      </div>
    );
  }

  const handleConnectBenimpos = async () => {
    const branchId = benimpos.branch_id.trim();
    const apiKey = benimpos.api_key.trim();
    const secretKey = benimpos.secret_key.trim();
    if (!branchId || !apiKey || !secretKey) {
      toast.error("Branch-ID, Api Key ve Secret Key zorunludur.");
      return;
    }
    setBenimposBusy(true);
    try {
      await api.post("/benimpos/connect", { branch_id: branchId, api_key: apiKey, secret_key: secretKey });
      const statusRes = await api.get("/benimpos/status").catch(() => ({}));
      setBenimpos((prev) => ({
        ...prev,
        connected: Boolean(statusRes?.connected),
        connected_at: statusRes?.connected_at || null,
        branch_id: statusRes?.branch_id || branchId,
        api_key: "",
        secret_key: "",
      }));
      toast.success("BenimPOS bağlantısı aktif edildi.");
    } catch (e) {
      toast.error(e?.message || "BenimPOS bağlantısı kurulamadı.");
    } finally {
      setBenimposBusy(false);
    }
  };

  const handleDisconnectBenimpos = async () => {
    setBenimposBusy(true);
    try {
      await api.post("/benimpos/disconnect");
      setBenimpos({ connected: false, connected_at: null, branch_id: "", api_key: "", secret_key: "" });
      toast.success("BenimPOS bağlantısı kesildi.");
    } catch (e) {
      toast.error(e?.message || "Bağlantı kesilemedi.");
    } finally {
      setBenimposBusy(false);
    }
  };

  const handleTestBenimpos = async () => {
    setBenimposBusy(true);
    try {
      await api.post("/benimpos/test");
      toast.success("BenimPOS bağlantısı başarılı.");
    } catch (e) {
      toast.error(e?.message || "Bağlantı testi başarısız.");
    } finally {
      setBenimposBusy(false);
    }
  };

  const handleConnectTurkcell = async () => {
    const apiKey = turkcellEfatura.api_key.trim();
    if (!apiKey) {
      toast.error("API Key zorunludur.");
      return;
    }
    setTurkcellBusy(true);
    try {
      await api.post("/turkcell-efatura/connect", {
        api_key: apiKey,
        test_mode: turkcellEfatura.test_mode_input,
      });
      const statusRes = await api.get("/turkcell-efatura/status").catch(() => ({}));
      setTurkcellEfatura((prev) => ({
        ...prev,
        connected: Boolean(statusRes?.connected),
        connected_at: statusRes?.connected_at || null,
        test_mode: Boolean(statusRes?.test_mode),
        api_key_masked: statusRes?.api_key_masked || null,
        api_key: "",
      }));
      toast.success("Turkcell e-Fatura bağlantısı aktif edildi.");
    } catch (e) {
      toast.error(e?.message || "Turkcell e-Fatura bağlantısı kurulamadı.");
    } finally {
      setTurkcellBusy(false);
    }
  };

  const handleDisconnectTurkcell = async () => {
    setTurkcellBusy(true);
    try {
      await api.post("/turkcell-efatura/disconnect");
      setTurkcellEfatura({ connected: false, connected_at: null, test_mode: false, api_key_masked: null, api_key: "", test_mode_input: false });
      toast.success("Turkcell e-Fatura bağlantısı kesildi.");
    } catch (e) {
      toast.error(e?.message || "Bağlantı kesilemedi.");
    } finally {
      setTurkcellBusy(false);
    }
  };

  const handleTestTurkcell = async () => {
    setTurkcellBusy(true);
    try {
      await api.post("/turkcell-efatura/test");
      toast.success("Turkcell e-Fatura bağlantısı başarılı.");
    } catch (e) {
      toast.error(e?.message || "Bağlantı testi başarısız.");
    } finally {
      setTurkcellBusy(false);
    }
  };

  const handleConnectParasut = async () => {
    try {
      const authUrl = await startParasutConnect();
      window.location.href = authUrl;
    } catch (e) {
      toast.error(e?.message || "Paraşüt bağlantısı başlatılamadı.");
    }
  };

  const handleConnectBizimhesap = async () => {
    const singleToken = String(bizimhesap.token || bizimhesap.api_key || "").trim();
    if (!singleToken) {
      toast.error("Token zorunludur.");
      return;
    }
    setBizimhesapBusy(true);
    try {
      const connectRes = await api.post("/bizimhesap/connect", {
        token: singleToken,
        firm_id: singleToken,
      });
      const statusRes = await api.get("/bizimhesap/status").catch(() => ({}));
      setBizimhesap((prev) => ({
        ...prev,
        connected: Boolean(statusRes?.connected),
        connected_at: statusRes?.connected_at || null,
        has_credentials: Boolean(statusRes?.has_credentials),
        has_token: Boolean(statusRes?.has_token),
        token_masked: String(statusRes?.token_masked || ""),
        last_test_ok: statusRes?.last_test_ok ?? null,
        last_test_error: String(statusRes?.last_test_error || ""),
      }));
      if (connectRes?.test_ok === false) {
        toast.error(connectRes?.test_error || "Bağlantı kaydedildi, test başarısız.");
      } else {
        toast.success("BizimHesap bağlantısı aktif edildi.");
      }
    } catch (e) {
      toast.error(e?.message || "BizimHesap bağlantısı kurulamadı.");
    } finally {
      setBizimhesapBusy(false);
    }
  };

  const handleDisconnectBizimhesap = async () => {
    setBizimhesapBusy(true);
    try {
      await api.post("/bizimhesap/disconnect");
      setBizimhesap((prev) => ({
        ...prev,
        connected: false,
        connected_at: null,
        has_credentials: false,
        has_token: false,
        token: "",
        token_masked: "",
        last_test_ok: null,
        last_test_error: "",
      }));
      toast.success("BizimHesap bağlantısı kesildi.");
    } catch (e) {
      toast.error(e?.message || "Bağlantı kesilemedi.");
    } finally {
      setBizimhesapBusy(false);
    }
  };

  const handleTestBizimhesap = async () => {
    setBizimhesapBusy(true);
    try {
      const result = await api.post("/bizimhesap/test");
      if (result?.success) {
        const count = Number(result?.warehouses_count || 0);
        toast.success(result?.message || `Bağlantı başarılı (${count} depo)`);
      } else {
        toast.error(result?.message || "Bağlantı testi başarısız.");
      }
    } catch (e) {
      toast.error(e?.message || "Bağlantı testi başarısız.");
    } finally {
      setBizimhesapBusy(false);
    }
  };

  return (
    <div className="page-shell space-y-5">
      <div className="page-header">
        <div className="app-page-header">
          <div className="flex items-start gap-4">
            {app.logo ? (
              <span className="inline-flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-border bg-white p-2 shadow-sm">
                <img src={app.logo} alt={`${app.name} logosu`} className="h-full w-full object-contain" />
              </span>
            ) : null}
            <div className="min-w-0">
              <h1 className="app-page-title">{app.name} Entegrasyonu</h1>
              <p className="app-page-subtitle">{app.longDescription || app.description}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_0.6fr]">
        <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <h2 className="text-base font-semibold text-foreground">Nasıl Çalışır?</h2>
          {isParasut ? (
            <div className="mt-3 space-y-3 text-sm leading-6 text-muted-foreground">
              <p>
                Paraşüt entegrasyonu ile ObiHub üzerindeki sipariş, fatura ve muhasebe süreçleri tek akışta yönetilir.
                Bağlantı kurulduğunda firma bilgileri otomatik okunur ve fatura süreçleri Paraşüt ile senkron çalışır.
              </p>
              <p>
                Entegrasyon ayarlarında depo, hesap ve fatura kategorisi seçerek operasyonu netleştirebilirsiniz.
                Bu sayede siparişten faturalandırmaya kadar tüm adımlar tutarlı ilerler.
              </p>
              <p>
                Bağlantı durumunu ve bağlı firma bilgisini <strong>Firmalarım</strong> ekranından takip edebilir,
                bağlantıyı test edip gerektiğinde güvenli şekilde kesebilirsiniz.
              </p>
            </div>
          ) : isBizimhesap ? (
            <div className="mt-3 space-y-3 text-sm leading-6 text-muted-foreground">
              <p>
                BizimHesap entegrasyonu ile ObiHub üzerindeki siparişlerinizi muhasebe akışına bağlayabilirsiniz.
                Özellikle WooCommerce ve Trendyol siparişlerinden fatura oluşturma süreci tek panelde yönetilir.
              </p>
              <p>
                Bu sayfada Token (Firma ID) girerek bağlantıyı aktif edebilir, bağlantıyı anlık test edip depo ve ürün
                verilerinin okunabildiğini doğrulayabilirsiniz.
              </p>
            </div>
          ) : isBenimpos ? (
            <div className="mt-3 space-y-3 text-sm leading-6 text-muted-foreground">
              <p>
                BenimPOS entegrasyonu ile WooCommerce ve pazaryeri siparişleriniz otomatik olarak BenimPOS'a aktarılır.
                Her sipariş için müşteri kartı oluşturulur veya güncellenir, satış kaydı açılır ve stok düşümü yapılır.
              </p>
              <p>
                BenimPOS üzerinde aktif olan e-Fatura entegrasyonunuz (Mysoft, EDM, eLogo veya E-Dönüştür) aynı anda
                çalışmaya devam eder; ObiHub bu akışa müdahil olmaz, sadece veriyi BenimPOS'a taşır.
              </p>
              <p>
                Bağlantı için BenimPOS &gt; Entegrasyonlar &gt; API Entegrasyonu ekranındaki
                <strong> Branch-ID</strong>, <strong>Api Key</strong> ve <strong>Secret Key</strong> değerlerini kullanın.
              </p>
            </div>
          ) : isTurkcellEfatura ? (
            <div className="mt-3 space-y-3 text-sm leading-6 text-muted-foreground">
              <p>
                Turkcell e-Şirket entegrasyonu ile ObiHub'daki siparişlerinizden doğrudan <strong>e-Fatura</strong> veya{" "}
                <strong>e-Arşiv</strong> faturası oluşturun ve GİB'e iletin. Gönderilen ve gelen faturalar tek panelde
                listelenir.
              </p>
              <p>
                Bağlantı için Turkcell e-Şirket portali &gt; API Yönetimi ekranından bir API Key oluşturun.
                Canlı ortam için{" "}
                <strong>portal.turkcellesirket.com</strong>'u, test ortamı için{" "}
                <strong>portaltest.isim360.com</strong>'u kullanın.
              </p>
              <p>
                Test modunu aktif ederek entegrasyonunuzu gerçek ortama geçmeden önce doğrulayabilirsiniz. Test
                ortamındaki işlemler GİB'e iletilmez.
              </p>
            </div>
          ) : (
            <>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                Uygulamayı bağladıktan sonra ilgili entegrasyon için kurulum adımlarını tamamlayarak sipariş, fatura ve muhasebe süreçlerini birlikte yönetebilirsiniz.
              </p>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                Uygulamalar mimarisi ile yeni sağlayıcılar aynı standart ekran, bağlantı adımı ve durum yönetimiyle sisteme eklenir.
              </p>
            </>
          )}
        </section>

        <aside className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          {isActive ? (
            <div className="space-y-2">
              {isParasut ? (
                loading ? (
                  <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Durum kontrol ediliyor...
                  </div>
                ) : parasut.connected ? (
                  <>
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                      Paraşüt bağlantısı aktif.
                      {parasut.company_id ? ` Şirket ID: ${parasut.company_id}` : ""}
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate("/panel/hesabim/firmalarim")}
                      className="h-11 w-full rounded-xl border border-border bg-card px-4 text-sm font-semibold text-foreground/80 hover:bg-muted/50"
                    >
                      Firmalarımı Görüntüle
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleConnectParasut}
                    className="h-11 w-full rounded-xl bg-[#ef4444] px-4 text-sm font-semibold text-white hover:opacity-95"
                  >
                    Paraşüt'e Bağlan
                  </button>
                )
              ) : null}
              {isBizimhesap ? (
                loading ? (
                  <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Durum kontrol ediliyor...
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className={`rounded-xl border p-3 text-sm ${bizimhesap.connected ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-border bg-muted/50 text-foreground/80"}`}>
                      {bizimhesap.connected ? "BizimHesap bağlantısı aktif." : "BizimHesap henüz bağlı değil."}
                    </div>
                    {bizimhesap.connected && bizimhesap.last_test_ok === false && bizimhesap.last_test_error ? (
                      <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                        Son test hatası: {bizimhesap.last_test_error}
                      </div>
                    ) : null}
                    {bizimhesap.connected && bizimhesap.token_masked ? (
                      <div className="rounded-xl border border-border bg-muted/50 p-3 text-xs text-muted-foreground">
                        Kayıtlı token: <span className="font-mono">{bizimhesap.token_masked}</span>
                      </div>
                    ) : null}

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-muted-foreground">Token (Firma ID)</label>
                      <input
                        type="text"
                        value={bizimhesap.token}
                        onChange={(e) => setBizimhesap((prev) => ({ ...prev, token: e.target.value }))}
                        placeholder="BizimHesap Token / Firma ID"
                        className="h-10 w-full rounded-xl border border-border px-3 text-sm text-slate-800 outline-none focus:border-[#1487a3]"
                        autoComplete="off"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleConnectBizimhesap}
                      disabled={bizimhesapBusy}
                      className="h-11 w-full rounded-xl bg-[#1487a3] px-4 text-sm font-semibold text-white hover:bg-[#1f79ad] disabled:opacity-70"
                    >
                      {bizimhesapBusy ? "İşleniyor..." : "BizimHesap'a Bağlan"}
                    </button>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={handleTestBizimhesap}
                        disabled={bizimhesapBusy || !bizimhesap.connected}
                        className="h-10 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-foreground/80 hover:bg-muted/50 disabled:opacity-60"
                      >
                        Bağlantıyı Test Et
                      </button>
                      <button
                        type="button"
                        onClick={handleDisconnectBizimhesap}
                        disabled={bizimhesapBusy || !bizimhesap.connected}
                        className="h-10 rounded-xl border border-rose-200 bg-rose-50 px-3 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                      >
                        Bağlantıyı Kes
                      </button>
                    </div>
                  </div>
                )
              ) : null}
              {isBenimpos ? (
                loading ? (
                  <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Durum kontrol ediliyor...
                  </div>
                ) : (
                  <div className="space-y-3">
                    {benimpos.connected ? (
                      <>
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                          BenimPOS bağlantısı aktif.{benimpos.branch_id ? ` (${benimpos.branch_id})` : ""}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={handleTestBenimpos}
                            disabled={benimposBusy}
                            className="h-10 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-foreground/80 hover:bg-muted/50 disabled:opacity-60"
                          >
                            Bağlantıyı Test Et
                          </button>
                          <button
                            type="button"
                            onClick={handleDisconnectBenimpos}
                            disabled={benimposBusy}
                            className="h-10 rounded-xl border border-rose-200 bg-rose-50 px-3 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                          >
                            Bağlantıyı Kes
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="rounded-xl border border-border bg-muted/50 p-3 text-sm text-foreground/80">
                          BenimPOS henüz bağlı değil.
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-semibold text-muted-foreground">Branch-ID (Mağaza Kodu)</label>
                          <input
                            type="text"
                            value={benimpos.branch_id}
                            onChange={(e) => setBenimpos((prev) => ({ ...prev, branch_id: e.target.value }))}
                            placeholder="Örn: U123456789"
                            className="h-10 w-full rounded-xl border border-border px-3 text-sm text-slate-800 outline-none focus:border-[#f97316]"
                            autoComplete="off"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-semibold text-muted-foreground">Api Key</label>
                          <input
                            type="password"
                            value={benimpos.api_key}
                            onChange={(e) => setBenimpos((prev) => ({ ...prev, api_key: e.target.value }))}
                            placeholder="BenimPOS Api Key"
                            className="h-10 w-full rounded-xl border border-border px-3 text-sm text-slate-800 outline-none focus:border-[#f97316]"
                            autoComplete="off"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-semibold text-muted-foreground">Secret Key</label>
                          <input
                            type="password"
                            value={benimpos.secret_key}
                            onChange={(e) => setBenimpos((prev) => ({ ...prev, secret_key: e.target.value }))}
                            placeholder="BenimPOS Secret Key"
                            className="h-10 w-full rounded-xl border border-border px-3 text-sm text-slate-800 outline-none focus:border-[#f97316]"
                            autoComplete="off"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleConnectBenimpos}
                          disabled={benimposBusy}
                          className="h-11 w-full rounded-xl bg-[#f97316] px-4 text-sm font-semibold text-white hover:bg-[#ea6c0a] disabled:opacity-70"
                        >
                          {benimposBusy ? "İşleniyor..." : "BenimPOS'a Bağlan"}
                        </button>
                      </>
                    )}
                  </div>
                )
              ) : null}
              {isTurkcellEfatura ? (
                loading ? (
                  <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Durum kontrol ediliyor...
                  </div>
                ) : (
                  <div className="space-y-3">
                    {turkcellEfatura.connected ? (
                      <>
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                          Turkcell e-Fatura bağlantısı aktif.
                          {turkcellEfatura.test_mode ? " (Test modu)" : " (Canlı)"}
                        </div>
                        {turkcellEfatura.api_key_masked ? (
                          <div className="rounded-xl border border-border bg-muted/50 p-3 text-xs text-muted-foreground">
                            API Key: <span className="font-mono">{turkcellEfatura.api_key_masked}</span>
                          </div>
                        ) : null}
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={handleTestTurkcell}
                            disabled={turkcellBusy}
                            className="h-10 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-foreground/80 hover:bg-muted/50 disabled:opacity-60"
                          >
                            Bağlantıyı Test Et
                          </button>
                          <button
                            type="button"
                            onClick={handleDisconnectTurkcell}
                            disabled={turkcellBusy}
                            className="h-10 rounded-xl border border-rose-200 bg-rose-50 px-3 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                          >
                            Bağlantıyı Kes
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="rounded-xl border border-border bg-muted/50 p-3 text-sm text-foreground/80">
                          Turkcell e-Fatura henüz bağlı değil.
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-semibold text-muted-foreground">API Key</label>
                          <input
                            type="password"
                            value={turkcellEfatura.api_key}
                            onChange={(e) => setTurkcellEfatura((prev) => ({ ...prev, api_key: e.target.value }))}
                            placeholder="Turkcell e-Şirket API Key"
                            className="h-10 w-full rounded-xl border border-border px-3 text-sm text-slate-800 outline-none focus:border-[#005f9e]"
                            autoComplete="off"
                          />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={turkcellEfatura.test_mode_input}
                            onChange={(e) => setTurkcellEfatura((prev) => ({ ...prev, test_mode_input: e.target.checked }))}
                            className="h-4 w-4 rounded border-border"
                          />
                          <span className="text-xs text-muted-foreground">Test modu (portaltest.isim360.com)</span>
                        </label>
                        <button
                          type="button"
                          onClick={handleConnectTurkcell}
                          disabled={turkcellBusy}
                          className="h-11 w-full rounded-xl bg-[#005f9e] px-4 text-sm font-semibold text-white hover:bg-[#004d82] disabled:opacity-70"
                        >
                          {turkcellBusy ? "İşleniyor..." : "Turkcell e-Fatura'ya Bağlan"}
                        </button>
                      </>
                    )}
                  </div>
                )
              ) : null}
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-muted/50 p-3 text-sm text-muted-foreground">
              Bu uygulama yakında aktif olacak. Hazır olduğunda Uygulamalar sayfasından bağlayabileceksiniz.
            </div>
          )}
          {isParasut && parasut.connected && parasut.firms?.length > 0 ? (
            <div className="mt-3 rounded-xl border border-border bg-muted/50 p-3">
              <p className="text-xs font-semibold text-foreground/80">Bağlı Firma</p>
              <p className="mt-1 text-sm text-slate-800">
                {parasut.firms.find((f) => f.is_selected)?.company_title || parasut.firms[0]?.company_title || "-"}
              </p>
              {parasut.account_email ? (
                <p className="mt-1 text-xs text-muted-foreground">{parasut.account_email}</p>
              ) : null}
            </div>
          ) : null}
          {isBenimpos && benimpos.connected ? (
            <div className="mt-3 rounded-xl border border-border bg-muted/50 p-3">
              <p className="text-xs font-semibold text-foreground/80">Bağlı Mağaza</p>
              <p className="mt-1 text-sm text-slate-800">{benimpos.branch_id || "-"}</p>
              {benimpos.connected_at ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(benimpos.connected_at).toLocaleDateString("tr-TR")} tarihinde bağlandı
                </p>
              ) : null}
            </div>
          ) : null}
          {isTurkcellEfatura && turkcellEfatura.connected ? (
            <div className="mt-3 rounded-xl border border-border bg-muted/50 p-3">
              <p className="text-xs font-semibold text-foreground/80">
                {turkcellEfatura.test_mode ? "Test Ortamı" : "Canlı Ortam"}
              </p>
              <p className="mt-1 text-sm text-slate-800">Turkcell e-Şirket</p>
              {turkcellEfatura.connected_at ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(turkcellEfatura.connected_at).toLocaleDateString("tr-TR")} tarihinde bağlandı
                </p>
              ) : null}
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
